import AmplifyButton from '@amplify/amplify-button';
import clsx from 'clsx';
import { BaseSyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EmptySlotDashIcon,
  HigherGradeEnvelopeIcon,
  K1EnvelopeIcon,
} from 'src/components/icons';
import { Tooltip } from 'src/components/tooltip';
import { NOTIFICATION_STALE_PERIOD_IN_DAYS } from 'src/constants/notifications.constants';
import { msAssignmentStatuses } from 'src/constants/xApi.constants';
import { CommonXApiPayload } from 'src/hooks/use-common-xapi-context-payload.hook';
import { useIsMiddleSchoolView } from 'src/hooks/use-is-middle-school-view';
import {
  CardstackAssignment,
  DesmosAssignment,
  MSAssignmentsStatusOptions,
  SingleAssignment,
} from 'src/interfaces/assignment.types';
import { TaughtLessonAssignment } from 'src/interfaces/class-stats.types';
import { XApiEventKeys } from 'src/interfaces/event.types';
import { Theme } from 'src/interfaces/theme.types';
import eventService from 'src/services/event.service';
import { Feedback, Score } from 'src/store/api/insights';
import { useCurrentGradeSelector } from 'src/store/features';
import { getAssignmentContent } from 'src/utils/assignments.utils';
import { getDayJSObject } from 'src/utils/date.utils';
import { isDesmosFeedback } from 'src/utils/feedback.utils';
import { getTheme } from 'src/utils/theme.utils';
import { generateAssignmentDataForEventPayload } from 'src/utils/xapi.utils';

import './assignment-feedback.scss';

type AssignmentFeedbackProps = {
  assignment:
    | SingleAssignment
    | TaughtLessonAssignment
    | DesmosAssignment
    | CardstackAssignment;
  commonXapiContextPayload: CommonXApiPayload;
  assignmentStatus:
    | MSAssignmentsStatusOptions.STARTED
    | MSAssignmentsStatusOptions.NOT_STARTED
    | MSAssignmentsStatusOptions.HANDED_IN;
  feedback?: Feedback;
  score?: Score;
};

export const AssignmentFeedback = ({
  feedback,
  score,
  assignment,
  commonXapiContextPayload,
  assignmentStatus,
}: AssignmentFeedbackProps) => {
  const { t } = useTranslation();

  const [isFeedbackVisible, setIsFeedbackVisible] = useState(false);
  const [destinationUrl, setDestinationUrl] = useState<string>();
  const [isRead, setIsRead] = useState(false);

  const { isHandedInmCLASSAssessment } = assignment;

  const isMiddleSchoolView = useIsMiddleSchoolView();

  const userCurrentGrade = useCurrentGradeSelector();
  const theme = getTheme(userCurrentGrade, isMiddleSchoolView);

  // handles cases when only score or feedback is present
  useEffect(() => {
    if (!score && feedback) {
      const totalCommentCount = feedback.insight_content.totalCommentCount;

      if (!totalCommentCount) return;

      if (feedback.insight_content.unreadCommentCount === 0) {
        setIsRead(true);
      }

      setDestinationUrl(feedback.insight_content.url);
      setIsFeedbackVisible(true);

      return;
    }

    if (score && !feedback) {
      if (!score.insight_content.score) return;

      // @TODO: start checking isScoreRead of Desmos Classroom teacher score once Desmos Classroom will start sending isScoreRead
      if (isDesmosFeedback(score.insight_content.url)) {
        const scoreTimestamp =
          score.updated_timestamp ?? score.created_timestamp;
        const scoreCreatedInDays = getDayJSObject().diff(scoreTimestamp, 'day');

        setIsRead(scoreCreatedInDays > NOTIFICATION_STALE_PERIOD_IN_DAYS);
      } else {
        setIsRead(!!score.insight_content.isScoreRead);
      }

      setDestinationUrl(score.insight_content.url);
      setIsFeedbackVisible(true);
    }
  }, [feedback, score]);

  // handles cases when both score and feedback are present
  useEffect(() => {
    if (!score?.insight_content.score || !feedback) return;

    const scoreTimestamp = score.updated_timestamp ?? score.created_timestamp;
    const feedbackTimestamp =
      feedback.updated_timestamp ?? feedback.created_timestamp;

    const totalCommentCount = feedback.insight_content.totalCommentCount;

    if (!totalCommentCount || scoreTimestamp > feedbackTimestamp) {
      // @TODO: start checking isScoreRead of Desmos Classroom teacher score once Desmos Classroom will start sending isScoreRead
      if (isDesmosFeedback(score.insight_content.url)) {
        const scoreTimestamp =
          score.updated_timestamp ?? score.created_timestamp;
        const scoreCreatedInDays = getDayJSObject().diff(scoreTimestamp, 'day');

        setIsRead(scoreCreatedInDays > NOTIFICATION_STALE_PERIOD_IN_DAYS);
      } else {
        setIsRead(!!score.insight_content.isScoreRead);
      }

      setDestinationUrl(score.insight_content.url);
      setIsFeedbackVisible(true);
      return;
    }

    if (feedback.insight_content.unreadCommentCount === 0) {
      setIsRead(true);
    }

    setDestinationUrl(feedback.insight_content.url);
    setIsFeedbackVisible(true);
  }, [feedback, score]);

  const handleFeedbackClick = async (e: BaseSyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isFeedbackVisible || !destinationUrl) return;

    try {
      await eventService.send(
        XApiEventKeys.selectedFeedback,
        {
          result: {
            destinationUrl: destinationUrl,
          },
        },
        {
          result: {
            isRightClick: e.type === 'contextmenu',
            ...commonXapiContextPayload,
            ...generateAssignmentDataForEventPayload({
              assignmentBusinessKey: assignment.assignmentBk,
              assignmentClassBk: assignment.assignmentClassBks,
              classGrade: userCurrentGrade,
              content: getAssignmentContent(assignment),
              assignmentStatus: msAssignmentStatuses[assignmentStatus],
            }),
          },
        },
      );
    } catch (error) {
      console.error('Error sending selected feedback xAPI event:', error);
    } finally {
      if (e.type !== 'contextmenu') {
        window.location.assign(destinationUrl);
      }
    }
  };

  return (
    <div
      className={clsx({
        feedback__container: isMiddleSchoolView,
        'feedback__container--disabled': isHandedInmCLASSAssessment,
      })}
    >
      {isMiddleSchoolView ? (
        <>
          {!isFeedbackVisible ? (
            <Tooltip
              title={t('No Feedback')}
              classNames={{
                tooltip: clsx('feedback__tooltip', {
                  'feedback__tooltip--hidden': isHandedInmCLASSAssessment,
                }),
              }}
              arrowDirection="up"
            >
              <EmptySlotDashIcon />
            </Tooltip>
          ) : (
            <Tooltip
              title={!isRead ? t('New Feedback') : t('Feedback')}
              classNames={{
                tooltip: clsx('feedback__tooltip', {
                  'feedback__tooltip--hidden': isHandedInmCLASSAssessment,
                }),
              }}
              arrowDirection="up"
            >
              <a
                onClick={handleFeedbackClick}
                onContextMenu={handleFeedbackClick}
                href={destinationUrl}
                className={clsx('feedback', {
                  feedback__read: isRead,
                  'feedback--disabled': isHandedInmCLASSAssessment,
                })}
                tabIndex={isHandedInmCLASSAssessment ? -1 : 0}
                aria-label="teacher-feedback"
                rel="noreferrer"
                target="_blank"
              />
            </Tooltip>
          )}
        </>
      ) : (
        <>
          {isFeedbackVisible && (
            <div
              className={clsx('theme-k5', {
                'layout-context-bravo': theme === Theme.K1,
                'layout-context-delta': theme !== Theme.K1,
              })}
            >
              <AmplifyButton
                variant="secondary"
                className={clsx(
                  {
                    'btn-raised': theme !== Theme.FOURTH_FIFTH,
                  },
                  'btn-icon',
                  `feedback--${theme}`,
                )}
                onClick={handleFeedbackClick}
                oval
                disabled={isHandedInmCLASSAssessment}
              >
                {theme === Theme.K1 ? (
                  <K1EnvelopeIcon isOpened={isRead} />
                ) : (
                  <HigherGradeEnvelopeIcon isOpened={isRead} />
                )}
              </AmplifyButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};
